/** @jsxImportSource @emotion/react */
import { ImmHelper } from "@base/util/Immutable";
import { PartialRecord } from "@base/util/TypeUtil";
import { SSTForm } from "@biz/definitions/SSTForm";
import {
    AxleType, CornerType, CSPositioning, IPatchData, IPoleData, ISunSailRopes, ISvgMeasureOptions, MaterialType, PointName, PoleId, PoleIndex, PoleMountType, PoleType, PylonType, SailBordersType, SunSailSides, SunSailType, SystemAxleVersionOld,
    TwistingSetType, WaterDirection
} from "@biz/definitions/SunSailDefinitions";
import { getPointName } from "@biz/definitions/SunSailDefinitions.util";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { SandyLogic } from "@biz/items/sunSail/logic/SandyLogic";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { createInitialSCPole, fillInitialPole } from "@biz/items/sunSail/SunSailInitial";
import { getSail2d3dReducers } from "@biz/items/sunSail/SunSailReducers.2d3d";
import { ArticleLogic } from "@biz/logic/ArticleLogic";
import { IClothData } from "@biz/logic/ClothData";
import { TriangulationHelper } from "@biz/logic/TriangulationHelper";
import { IObstacleData, ISail2dForm, SailCoilUpType, SailInputMode } from "@biz/models/Sail2dDefinitions";
import { getProductReducers, getSlotReducer } from "@biz/reducers/CommonReducers";
import { getEdgeReducers } from "@biz/reducers/EdgeReducer";
import { getNotesAndFilesReducer } from "@biz/reducers/NotesAndFilesReducer";
import { ISCPole } from "@biz/sc/sunSail/ISCSunSailData";

export const Sail2dReducers = {
  ...getNotesAndFilesReducer<ISail2dForm>(),
  ...getProductReducers<ISail2dForm>(),
  ...getEdgeReducers<ISail2dForm>(),
  ...getSail2d3dReducers<ISail2dForm>(),
  setPositionMode(old: ISail2dForm, positionMode: ISail2dForm["positionMode"]): ISail2dForm {
    return ImmHelper.setProperty(old, "positionMode", positionMode);
  },
  setProductSlot: getSlotReducer<ISail2dForm>(),
  setDistOut(old: ISail2dForm, poleId: PoleId, val: number | undefined): ISail2dForm {
    if (SSTForm.is(old, SSTForm.AMCXOneFlex) || SSTForm.is(old, SSTForm.CsXsTwin)) {
      const pointName = getPointName(poleId);
      old = {
        ...old,
        [`out${pointName}`]: val,
        [`inOut${pointName}Priority`]: val === undefined ? "in" : "out",
      };

      if (old.poleCount >= 4) {
        const otherPointName = getPointName(Poles.getOtherPole(old.sunSailType, poleId))
        old = {
          ...old,
          [`out${pointName}${otherPointName}Priority`]: val === undefined ? otherPointName : pointName
        }
      }
    }
    return old;
  },
  setDistIn(old: ISail2dForm, poleId: PoleId, val: number | undefined) {
    if (SSTForm.is(old, SSTForm.AMCXOneFlex) || SSTForm.is(old, SSTForm.CsXsTwin)) {
      const pointName = getPointName(poleId);
      old = {
        ...old,
        [`in${pointName}`]: val,
        [`inOut${pointName}Priority`]: val === undefined ? "out" : "in",
      };
      if (old.poleCount >= 4) {
        const otherPointName = getPointName(Poles.getOtherPole(old.sunSailType, poleId))
        old = {
          ...old,
          [`out${pointName}${otherPointName}Priority`]: val === undefined ? otherPointName : pointName
        };
      }
    }
    return old;
  },
  setCornerType(old: ISail2dForm, index: number, cornerType: CornerType) {
    if (ArticleLogic.isCornerForAll(old.sunSailType, cornerType)) {
      const newCorners: CornerType[] = [];
      for (let i = 0; i < old.poleCount; i++) {
        newCorners.push(cornerType);
      }
      return {
        ...old,
        cornerTypes: newCorners,
      };
    } else {
      const newCornerTypes = ImmHelper.setArrayItem(old.cornerTypes, index, cornerType)
      return {
        ...old,
        cornerTypes: newCornerTypes
      };
    }
  },
  setCS4PositionMode(old: ISail2dForm, index: number, positioningMode: CSPositioning) {
    if (old.sunSailType !== SunSailType.SolidayCS && old.sunSailType !== SunSailType.SolidayTwin) return old;
    return {
      ...old,
      cs4PositionMode: ImmHelper.setArrayItem(old.cs4PositionMode, index, positioningMode)
    }
  },
  setPylon(old: ISail2dForm, poleIndex: number, type: PylonType | undefined) {
    return {
      ...old,
      pylons: ImmHelper.setArrayItem(old.pylons, poleIndex, type)
    }
  },
  setInputMode(old: ISail2dForm, inputMode: SailInputMode) {
    return {
      ...old,
      inputMode
    }
  },
  setCoilUpType(old: ISail2dForm, coilUpType: SailCoilUpType) {
    return {
      ...old,
      coilUpType
    }
  },
  setCoilUpPole(old: ISail2dForm, coilUpPole: PointName) {
    return {
      ...old,
      coilUpPole
    }
  },
  setAxleType(old: ISail2dForm, axleType: AxleType | SystemAxleVersionOld | undefined) {
    return ImmHelper.setProperty(old, "axleType", axleType);
  },
  setHighestPoint(old: ISail2dForm, highestPoint: PointName) {
    return {
      ...old,
      highestPoint
    }
  },
  setCoilUpDeflectionPosition(old: ISail2dForm, coilUpDeflectionPosition: PoleIndex) {
    return ImmHelper.setProperty(old, "coilUpDeflectionPosition", coilUpDeflectionPosition);
  },
  setCoilUpDeflectionOpposite(old: ISail2dForm, val: ISail2dForm["coilUpDeflectionOpposite"]) {
    return ImmHelper.setProperty(old, "coilUpDeflectionOpposite", val);
  },
  setAlignDistOut(old: ISail2dForm, alignDistOut: boolean | undefined) {
    return {
      ...old,
      alignDistOut
    }
  },
  setTwistingSet(old: ISail2dForm, twistingSet?: TwistingSetType) {
    return {
      ...old,
      twistingSet
    }
  },
  setClothData(old: ISail2dForm, cloth: IClothData | undefined, colorId?: string): ISail2dForm {
    return {
      ...old,
      cloth,
      colorId
    }
  },
  setClothCount(old: ISail2dForm, count: number | undefined): ISail2dForm {
    return {
      ...old,
      clothCount: count === undefined ? 1 : count
    }
  },
  setSecurityIgnoreMinDistIn(old: ISail2dForm, flag: boolean): ISail2dForm {
    return {
      ...old,
      securityCheckOptions: {
        ...old.securityCheckOptions,
        ignoreMinDistIn: flag
      }
    }
  },
  setObstacles(old: ISail2dForm, obstacles: PartialRecord<SunSailSides, IObstacleData>): ISail2dForm {
    return ImmHelper.setProperty(old, "obstacles", obstacles)
  },
  toggleObstacleSide(old: ISail2dForm, side: SunSailSides): ISail2dForm {
    return ImmHelper.setProperty(old, "obstacles", {
      ...old.obstacles,
      [side]: old.obstacles?.[side] ? undefined : { side },
    })
  },
  setObstacleProp(old: ISail2dForm, obstacleData: IObstacleData): ISail2dForm {
    return ImmHelper.setProperty(old, "obstacles", {
      ...old.obstacles,
      [obstacleData.side!]: obstacleData
    })
  },
  setSailBordersType(old: ISail2dForm, borderType: SailBordersType): ISail2dForm {
    return ImmHelper.setProperty(old, "sailBordersType", borderType);
  },
  setWaterDirection(old: ISail2dForm, index: number, wd: WaterDirection): ISail2dForm {
    return ImmHelper.setProperty(old, "waterDirections", ImmHelper.setArrayItem(old.waterDirections, index, wd))
  },
  setRopeArticleId(old: ISail2dForm, name: keyof ISunSailRopes, articleId: string | undefined): ISail2dForm {
    if (!old[name])
      old = ImmHelper.setProperty(old, name, {})

    return ImmHelper.setPropertyNonNullable(old, name, "articleId", articleId)
  },
  setRopeLength(old: ISail2dForm, name: keyof ISunSailRopes, length: number | undefined): ISail2dForm {
    if (!old[name])
      old = ImmHelper.setProperty(old, name, {})

    return ImmHelper.setPropertyNonNullable(old, name, "length", length)
  },
  setSandyCenterTriangulationPoleId(old: ISail2dForm, centerPoleId: PoleId, triangulationIndex: number, triangulationPoleId: PoleId): ISail2dForm {
    const centerPole = Poles.fromPoleId(centerPoleId)
    if (centerPole.polePositionType !== "center")
      throw new Error("setSandyCenterDistanceTriangulationPoleId: Invalid centerPole pole: " + centerPoleId);
    return Sail2dReducers.setPoleData(old, centerPoleId, {}, (oldPole) => {
      let triangulation = oldPole.centerPoleTriangulation || TriangulationHelper.createInitialPoleTriangulationData();
      triangulation = ImmHelper.setArrayItem1(triangulation, triangulationIndex, ImmHelper.setProperty(triangulation[triangulationIndex], "poleId", triangulationPoleId));

      return ImmHelper.setProperty(oldPole, "centerPoleTriangulation", triangulation)
    });
  },
  setSandyCenterTriangulationDistance(old: ISail2dForm, centerPoleId: PoleId, triangulationIndex: number, distance: number): ISail2dForm {
    const centerPole = Poles.fromPoleId(centerPoleId)
    if (centerPole.polePositionType !== "center")
      throw new Error("setSandyCenterDistanceTriangulationPoleId: Invalid centerPole pole: " + centerPoleId);
    return Sail2dReducers.setPoleData(old, centerPoleId, {}, (oldPole) => {
      let triangulation = oldPole.centerPoleTriangulation || TriangulationHelper.createInitialPoleTriangulationData();
      triangulation = ImmHelper.setArrayItem1(triangulation, triangulationIndex, ImmHelper.setProperty(triangulation[triangulationIndex], "distance", distance));

      return ImmHelper.setProperty(oldPole, "centerPoleTriangulation", triangulation)
    });
  },
  setMeasureOption(old: ISail2dForm, name: keyof ISvgMeasureOptions, value: boolean): ISail2dForm {
    return {
      ...old,
      measureOptions: {
        ...old.measureOptions,
        [name]: value
      }
    }
  },
  // setPoleData<T extends any[]>(old: ISail2dForm, poleId: PoleId, fn: ((old: ISCPole, ...v: T) => ISCPole), ...val: T): ISail2dForm {
  //   if (!SSTForm.is(old, SSTForm.Sandy))
  //     throw new Error("SSTForm is not a Sandy")
  //   if (!Poles.isValid(old, poleId))
  //     throw new Error("SunSailReducer.setPoleData: Invalid pole: " + poleId);
  //   const newPole = fn(Poles.getPole(old, poleId), ...val)
  //   const pi = Poles.fromPoleId(poleId)
  //   old = ImmHelper.setPropertyNonNullable(old, Poles.getPolePositionProperty(pi.polePositionType), pi.poleIndex, newPole);
  //   return old;
  // },
  setPoleType(old: ISail2dForm, poleId: number, poleType: PoleType): ISail2dForm {
    return Sail2dReducers.setPoleData(old, poleId, {}, (pole) => ImmHelper.setProperty(pole, "poleType", poleType));
  },
  setMount(old: ISail2dForm, poleIndex: number, mount: PoleMountType): ISail2dForm {
    return Sail2dReducers.setPoleData(old, poleIndex, {}, (pole, mount) => {
      pole = ImmHelper.setProperty(pole, "mount", mount);
      pole = ImmHelper.setProperty(pole, "angle", SunSailLogic.calcNewInitialPoleAngle(ImmHelper.setProperty(old, "poles", poleIndex, pole), poleIndex));
      return pole;
    }, mount);
  },
  setMaterial(old: ISail2dForm, poleId: PoleId, material: MaterialType): ISail2dForm {
    return Sail2dReducers.setPoleData(old, poleId, {}, (pole, material) => {
      pole = ImmHelper.setProperty(pole, "material", material);
      pole = ImmHelper.setProperty(pole, "angle", SunSailLogic.calcNewInitialPoleAngle(ImmHelper.setProperty(old, "poles", poleId, pole), poleId));
      return pole;
    }, material);
  },

  setPoleArticleSlot<KEY extends keyof IPoleData["articleSlots"]>(old: ISail2dForm, poleId: PoleId, name: KEY, articleId: IPoleData["articleSlots"][KEY]): ISail2dForm {
    return Sail2dReducers.setPoleData(old, poleId, {}, (pole) => {
      const articleSlots = pole.articleSlots || {};
      const newArticleSlots = ImmHelper.setPropertyNonNullable(articleSlots, name, articleId);
      return ImmHelper.setProperty(pole, "articleSlots", newArticleSlots);
    });
  },

  setAdditionalPointAfterPoleId(old: ISail2dForm, poleId: PoleId, newPoleId: PoleId): ISail2dForm {
    if (!SSTForm.is(old, SSTForm.Sandy))
      throw new Error("addAdditionalPole: Not a Sandy")
    if (poleId === newPoleId)
      return old;
    old = SandyLogic.correctCornerPoleCountChange(old, poleId, -1);
    old = SandyLogic.correctCornerPoleCountChange(old, newPoleId, 1);
    const poles = ImmHelper.moveArrayItem(old.poles, poleId, newPoleId);
    old = {
      ...old,
      poles
    }
    return old;
  },

  setAdditionalPointDistance(old: ISail2dForm, poleId: PoleId, distance: number): ISail2dForm {
    if (!SSTForm.is(old, SSTForm.Sandy))
      throw new Error("setAdditionalPointDistance: Not a Sandy")
    old = {
      ...old,
      poles: ImmHelper.setArrayItem(old.poles, poleId, {
        ...old.poles[poleId],
        additionalPole: {
          ...old.poles[poleId].additionalPole,
          distance
        }
      })
    }
    return old;
  },
  addAdditionalPoint(old: ISail2dForm, afterPoleId: PoleId, distance: number): ISail2dForm {
    if (!SSTForm.is(old, SSTForm.Sandy))
      throw new Error("addAdditionalPole: Not a Sandy")

    const newPoleCount = old.poleCount + 1;
    const newPoleId = afterPoleId + 1;

    old = SandyLogic.correctCornerPoleCountChange(old, newPoleId, 1);
    old = {
      ...old,
      poles: ImmHelper.insertArrayItem(old.poles, newPoleId, createInitialSCPole(old, newPoleCount, old.poles.length, old.featureLevel))
    }
    const p: ISCPole = {
      ...fillInitialPole(old, newPoleId),
      additionalPole: {
        distance
      }
    }
    old.poles[newPoleId] = p;
    old.poleCount = newPoleCount;
    return old;
  },
  removeAdditionalPoint(old: ISail2dForm, id: PoleId): ISail2dForm {
    if (!SSTForm.is(old, SSTForm.Sandy))
      throw new Error("addAdditionalPole: Not a Sandy")
    old = SandyLogic.correctCornerPoleCountChange(old, id, -1);
    return {
      ...old,
      poles: ImmHelper.removeArrayItem(old.poles, id),
      poleCount: old.poleCount - 1
    }
  },

  addCenterPole(old: ISail2dForm): ISail2dForm {
    if (!SSTForm.is(old, SSTForm.Sandy))
      throw new Error("addCenterPole: Not a Sandy")
    if (!old.dimensions) {
      old = {
        ...old,
        dimensions: SandyLogic.calcInitialSandyDimensions(old.sunSailType, old.poleCount)
      }
    }
    return {
      ...old,
      centerPoles: [
        ...(old.centerPoles || []),
        {
          poleType: PoleType.Pole,
          sandyOperatingRopePoleId: PoleIndex.A,
          centerPoleTriangulation: [
            ...TriangulationHelper.createInitialPoleTriangulationData(old.dimensions.ab ? old.dimensions.ab * 0.6 : 1)
          ]
        },
      ]
    }
  },

  removeCenterPole(old: ISail2dForm, poleId: PoleId): ISail2dForm {
    if (!SSTForm.is(old, SSTForm.Sandy))
      throw new Error("addCenterPole: Not a Sandy")
    const pole = Poles.fromPoleId(poleId);
    if (pole.polePositionType !== "center")
      throw new Error("removeCenterPole: Not a center pole: " + poleId);
    return {
      ...old,
      centerPoles: old.centerPoles?.filter((_, i) => i !== pole.poleIndex)
    }
  },

  addPatch(old: ISail2dForm, patch: IPatchData | undefined): ISail2dForm {
    return SandyLogic.addPatch(old, patch);
  },
  removePatch(old: ISail2dForm, patchIndex: number): ISail2dForm {
    if (old.patches) {
      const newPatches = ImmHelper.removeArrayItem(old.patches, patchIndex)
      return {
        ...old,
        patches: newPatches
      }
    }
    return old;
  },
}